import { CompactBand, BandMintLink, BandTab, TrainBar } from '@/components/CompactBand';
import type { Settings } from '../types';
import { TRAINING_GOAL, countWord, formatMoney } from '../utils';

interface HeaderProps {
  mode: 'training' | 'autopilot';
  settings: Settings;
  waitingCount: number;
  waitingTotal: number;
  readyCount: number;
  needsCount: number;
  readyTotal: number;
  milestoneDismissed: boolean;
  onReopenMilestone: () => void;
}

/** "One needs you." / "Two need you." (amber em; needsCount is never 0 here). */
function needsClause(n: number): string {
  return n === 1 ? 'One needs you.' : `${countWord(n)} need you.`;
}

export function Header({
  mode,
  settings,
  waitingCount,
  waitingTotal,
  needsCount,
}: HeaderProps) {
  const confirmed = settings.confirmed;
  const atMilestone = confirmed >= TRAINING_GOAL;

  // Right side: once autopilot is available (at milestone) or on, a plain status
  // + "Change" link; otherwise the training bar + "How this works".
  const autopilotReady = mode === 'autopilot' || atMilestone;
  const right = autopilotReady ? (
    <>
      <span>{mode === 'autopilot' ? 'Autopilot on' : 'Autopilot off'}</span>
      <BandMintLink to="/settings/autopilot">Change</BandMintLink>
    </>
  ) : (
    <>
      <TrainBar confirmed={confirmed} goal={TRAINING_GOAL} />
      <BandMintLink to="/settings/autopilot">How this works</BandMintLink>
    </>
  );

  let headline: React.ReactNode;
  let mint = false;

  if (mode === 'autopilot') {
    if (waitingCount === 0) {
      headline = (
        <>
          Nothing needs you. <em>ELVA submitted the rest.</em>
        </>
      );
      mint = true;
    } else {
      headline = (
        <>
          {countWord(waitingCount)} claim{waitingCount === 1 ? '' : 's'} need you.{' '}
          <em>ELVA submitted the rest.</em>
        </>
      );
    }
  } else if (waitingCount === 0) {
    headline = <>No claims are waiting.</>;
  } else if (waitingCount > 200) {
    // An over-200 queue is an operations signal, not a list problem (D148 §2).
    headline = (
      <>
        {waitingCount} claims are waiting; <em>autopilot is off.</em>
      </>
    );
  } else {
    // The count equals the sidebar Claims count — every waiting claim (rcm-02 §3).
    headline = (
      <>
        {waitingCount} claim{waitingCount === 1 ? '' : 's'} {waitingCount === 1 ? 'is' : 'are'} ready to send,{' '}
        {formatMoney(waitingTotal)}.{needsCount > 0 ? <> <em>{needsClause(needsCount)}</em></> : null}
      </>
    );
  }

  const tabs = (
    <>
      <BandTab label="Needs you" to="/claims" active />
      <BandTab label="All claims" to="/claims/all" />
    </>
  );

  return <CompactBand headline={headline} mint={mint} right={right} tabs={tabs} />;
}
